import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar, { ForeignKeyOption } from '../../components/FilterBar';
import { userFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal, SkeletonTable } from '../../components/ui';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/PageHeader';
import { translateError } from '../../utils/errorMessages';

interface IUser {
  _id: string;
  name: string;
  email: string;
  role?: { _id: string; name: string; type?: 'admin' | 'user' };
}

interface IMember {
  _id: string;
  user?: { _id: string };
}

const UsersListPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [users, setUsers] = useState<IUser[]>([]);
  const [members, setMembers] = useState<IMember[]>([]);
  const [roles, setRoles] = useState<{ _id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValue[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Permission checks
  const canCreate = hasPermission('users:write');
  const canEdit = hasPermission('users:write');
  const canDelete = hasPermission('users:delete');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersResponse, membersResponse] = await Promise.all([
        api.users.list(),
        api.members.list(),
      ]);
      setUsers(usersResponse.data as IUser[]);
      setMembers(membersResponse.data as IMember[]);
      // Roles power the role filter dropdown (non-critical — ignore failure).
      try {
        const rolesResponse = await api.roles.list();
        setRoles((rolesResponse.data as { _id: string | number; name: string }[]).map(r => ({ _id: String(r._id), name: r.name })));
      } catch {
        // Ignore — user might not have permission to list roles.
      }
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMemberCount = useCallback((userId: string): number => {
    return members.filter(m => m.user && m.user._id === userId).length;
  }, [members]);

  const handleDeleteClick = (user: IUser) => {
    if (!canDelete) return;
    setDeleteId(user._id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await api.users.delete(deleteId);
      fetchData();
      addToast(t('users.deleteSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'users.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const columns: Column<IUser>[] = useMemo(() => [
    {
      header: t('users.name'),
      key: 'name',
    },
    {
      header: t('common.email'),
      key: 'email',
    },
    {
      header: t('users.role'),
      sortValue: (user) => user.role ? user.role.name : null,
      render: (user) => user.role ? (
        <span className={user.role.type === 'admin' ? 'badge badge-admin' : 'badge'}>{user.role.name}</span>
      ) : '',
    },
    {
      header: t('users.membersCount'),
      sortValue: (user) => getMemberCount(user._id),
      render: (user) => getMemberCount(user._id),
    },
  ], [t, getMemberCount]);

  const filteredUsers = useMemo(() => applyFilters(users, filters), [users, filters]);

  const foreignKeyOptions = useMemo<Record<string, ForeignKeyOption[]>>(() => ({
    roles: roles.map(r => ({ value: r._id, label: r.name })),
  }), [roles]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <PageHeader
        title={t('users.title')}
        helpKey="help.admin.users"
        action={canCreate ? <Link to="/admin/users/create" className="btn btn--primary btn--md">{t('users.createNew')}</Link> : undefined}
      />

      <FilterBar fields={userFields} onFilterChange={handleFilterChange} foreignKeyOptions={foreignKeyOptions} />

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : filteredUsers.length === 0 ? (
        <EmptyState
          title={t('users.emptyTitle')}
          description={t('users.emptyDescription')}
          action={canCreate ? { label: t('users.createNew'), href: '/admin/users/create' } : undefined}
        />
      ) : (
        <DataTable
          data={filteredUsers}
          columns={columns}
          editLink={canEdit ? (user) => `/admin/users/${user._id}/edit` : undefined}
          onDelete={canDelete ? handleDeleteClick : undefined}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('users.deleteTitle')}
        message={t('users.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default UsersListPage;
